import React, { useState, useCallback, useEffect } from 'react';
import { Typography, Modal, Box, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faEnvelope, faPhoneAlt, faUsers, faTimesCircle, faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { faCalendarAlt, faClock, faClipboard } from "@fortawesome/free-regular-svg-icons";
import { faGoogle, faMicrosoft } from "@fortawesome/free-brands-svg-icons";
import { isMobile } from "react-device-detect";
import { createEvent } from "ics";
import { saveAs } from "file-saver";
import clsx from "clsx";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    modalContainer: {
        [theme.breakpoints.up('sm')]: {
            width: '50%',
        },
        [theme.breakpoints.up('lg')]: {
          width: '35%',
        },
        height: '45%',
        width: '90%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
    spacing1: {
        paddingTop: theme.spacing(1),
    },
    spacing2: {
        paddingTop: theme.spacing(2),
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    container: {
        width: '75%',
    },
    textWrap: {
        wordWrap: 'break-word',
    },
    webLink: {
        color: 'inherit',
        textDecoration: 'none',
        cursor: 'pointer',
        width: '100%',
    },
    iconSpace: {
        paddingRight: theme.spacing(1),
    },
    iconColor: {
        color: theme.palette.primary.main,
    },
    calendarButton: {
        [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
        margin: theme.spacing(2),
        borderRadius: theme.spacing(3),
        border: '2px solid',
        borderColor: theme.palette.primary.main,
        textAlign: 'center',
        cursor: 'pointer',
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    closeButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingBottom: theme.spacing(1),
    },
}));

const ModuleList = ({ data, mainDisplayColumns, altDisplayColumn }) => {
    const classes = useStyles();
    const theme = useTheme();

    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        height: isMd ? '40%' : '50%',
        // height: isMd ? '50%' : '65%',
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const [title, setTitle] = useState('');
    const [formatMobilePhoneNumber, setFormatMobilePhoneNumber] = useState('');
    const [formatOfficePhoneNumber, setFormatOfficePhoneNumber] = useState('');
    const [formatStreetAddress, setFormatStreetAddress] = useState('');
    const [formatCityStateZip, setFormatCityStateZip] = useState('');
    const [companyTitle, setCompanyTitle] = useState('');
    const [startTime, setStartTime] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [utcTime, setUtcTime] = useState('');
    const [icsFileDateAndTime, setIcsFileDateAndTime] = useState([]);
    const [icsDuration, setIcsDuration] = useState({});
    const [googleCalendarEvent, setGoogleCalendarEvent] = useState('');
    const [googleCalendarMobileEvent, setGoogleCalendarMobileEvent] = useState('');
    const [outlookCalendarLiveEvent, setOutlookCalendarLiveEvent] = useState('');
    const [outlookCalendarOfficeEvent, setOutlookCalendarOfficeEvent] = useState('');
    const [openEventModal, setOpenEventModal] = useState(false);

    const formatData = useCallback(() => {
        let tempTitle = '';
        React.Children.toArray(mainDisplayColumns.forEach((column) => {
            if (data[column] && tempTitle.length === 0) {
                tempTitle = data[column];
            } else if (data[column] && tempTitle.length > 0) {
                tempTitle = tempTitle.concat(' ', data[column]);
            }
        }))
        if (!tempTitle) {
            if (data[altDisplayColumn]) {
                tempTitle = data[altDisplayColumn];
            }
        }
        setTitle(tempTitle);

        if (data.mobilePhone) {
            let phoneNumber = '';
            if (data.mobilePhone[0] === "1") {
                let areaCode = '(',
                    remainingPhoneDigits = '';
                areaCode = areaCode.concat(data.mobilePhone.slice(1, 4), ")");
                remainingPhoneDigits = data.mobilePhone.slice(4, 7).concat("-", data.mobilePhone.slice(7));
                phoneNumber = areaCode.concat(" ", remainingPhoneDigits);
            }
            setFormatMobilePhoneNumber(phoneNumber);
        } 

        if (data.officePhone) {
            let phoneNumber = '';
            if (data.officePhone[0] === "1") {
                let areaCode = '(',
                    remainingPhoneDigits = '';
                areaCode = areaCode.concat(data.officePhone.slice(1, 4), ")");
                remainingPhoneDigits = data.officePhone.slice(4, 7).concat("-", data.officePhone.slice(7));
                phoneNumber = areaCode.concat(" ", remainingPhoneDigits);
            }
            setFormatOfficePhoneNumber(phoneNumber);
        }

        if (data.title && data.categoryTitle) {
            setCompanyTitle(data.title.concat(", ", data.categoryTitle));
        } else if (data.title) {
            setCompanyTitle(data.title);
        } else {
            setCompanyTitle(data.categoryTitle);
        }

        const hasAddress = data.fullAddress || data.address ? true : false;
        let address = '';
        if (hasAddress) {
            address = data.fullAddress ? data.fullAddress : data.address;
            const index = address.indexOf(',');
            const formattedStreetAddress = address.slice(0, index);
            const formattedCityStateZip = address.slice(index + 2);
            setFormatStreetAddress(formattedStreetAddress);
            setFormatCityStateZip(formattedCityStateZip);
        } else {
            setFormatStreetAddress('');
            setFormatCityStateZip('');
        }

        let googleCalendarLink = 'https://www.google.com/calendar/render?action=TEMPLATE&',
            googleCalendarLinkMobile = 'https://calendar.google.com/calendar/u/0/gp#~calendar:view=e&bm=1',
            outlookCalendarOfficeLink = 'https://outlook.office.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&',
            outlookCalendarLiveLink = 'https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&',
            formatStartDate = '',
            formatStartTime = '',
            formatEndDate = '',
            formatEndTime = '',
            icsStartTime,
            icsEndTime;
        if (data.startDate) {
            formatStartDate = moment(data.startDate).format('MM/DD/YYYY');
            formatStartTime = moment(data.startDate).format('hh:mm a');
            // let icsDate = moment(data.startDate).format('YYYY, MM, DD, H, m');
            // icsStartTime = moment(data.startDate).format('H, m');
            setStartDate(formatStartDate);
            setStartTime(formatStartTime);
            // icsDate = icsDate.split(',')
            // const icsFileArray = [];
            // for (let i = 0; i < icsDate.length; i++) {
            //     if (!isNaN(icsDate[i])) {
            //         const newInt = parseInt(icsDate[i]);
            //         icsFileArray.push(newInt);
            //     }
            // }
            // setIcsFileDateAndTime(icsFileArray);
        }
        if (data.endDate) {
            formatEndDate = moment(data.endDate).format('MM/DD/YYYY');
            formatEndTime = moment(data.endDate).format('hh:mm a');
            // icsEndTime = moment(data.endDate).format('H, m');
            setEndDate(formatEndDate);
            setEndTime(formatEndTime);
        }
        if (data.startDate && data.endDate) {
            // const icsStartTimeSplit = icsStartTime.split(',');
            // const icsEndTimeSplit = icsEndTime.split(',');
            // let hours = 0,
            //     minutes = 0;
            // hours = icsEndTimeSplit[0] - icsStartTimeSplit[0];
            // minutes = icsEndTimeSplit[1] - icsStartTimeSplit[1];
            // if (minutes < 0) {
            //     hours--;
            //     minutes+=60;
            // }
            // setIcsDuration({ hours: hours, minutes: minutes });
            const startDateWithStartTime = formatStartDate.concat(' ', formatStartTime);
            let startDateWithEndTime = formatStartDate.concat(' ', formatEndTime);
            if (moment(startDateWithStartTime).isAfter(startDateWithEndTime)) {
                startDateWithEndTime = moment(startDateWithEndTime, "MM-DD-YYYY, hh:mm a").add(1, 'd');
            }
            const convertStartDateWithEndTime = new Date(startDateWithEndTime).toISOString().replace(/-|:|\.\d\d\d/g,"");
            const convertStartTime = new Date(data.startDate).toISOString().replace(/-|:|\.\d\d\d/g,"");
            const convertEndTime = new Date(data.endDate).toISOString().replace(/-|:|\.\d\d\d/g,"");
            const outlookStartTime = new Date(data.startDate).toISOString();
            const outlookEndTime = new Date(data.endDate).toISOString();
            // setUtcTime(convertEndTime);
            googleCalendarLink = googleCalendarLink.concat('', `text=${tempTitle}&dates=${convertStartTime}/${convertStartDateWithEndTime}&location=${address}&details=${data[altDisplayColumn]}&recur=RRULE:FREQ=DAILY;UNTIL=${convertEndTime}`);
            googleCalendarLinkMobile = googleCalendarLinkMobile.concat('', `text=${tempTitle}&dates=${convertStartTime}/${convertStartDateWithEndTime}&location=${address}&details=${data[altDisplayColumn]}&recur=RRULE:FREQ=DAILY;UNTIL=${convertEndTime}`);
            outlookCalendarOfficeLink = outlookCalendarOfficeLink.concat('', `startdt=${outlookStartTime}&enddt=${outlookEndTime}&subject=${tempTitle}&location=${address}&body=${data[altDisplayColumn]}`);
            outlookCalendarLiveLink = outlookCalendarLiveLink.concat('', `startdt=${outlookStartTime}&enddt=${outlookEndTime}&subject=${tempTitle}&location=${address}&body=${data[altDisplayColumn]}`);
            setGoogleCalendarEvent(googleCalendarLink);
            setGoogleCalendarMobileEvent(googleCalendarLinkMobile);
            setOutlookCalendarOfficeEvent(outlookCalendarOfficeLink);
            setOutlookCalendarLiveEvent(outlookCalendarLiveLink);
        }
    }, [altDisplayColumn, data, mainDisplayColumns]);

    useEffect(() => {
        formatData();
    }, [data, formatData]);

    return (
        <div className={classes.root}>
            <Typography variant="h5" color="primary">{title}</Typography>

            {
                (data.title || data.company) && (
                    <div className={classes.spacing1}>
                        <Typography variant="subtitle2">{companyTitle}</Typography>
                    </div>
                )
            }

            {(data.fullAddress || data.address) && (
                <div className={classes.spacing2}>
                    <a 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        href={`http://maps.google.com/?q=${data.fullAddress ? data.fullAddress : data.address}`} 
                        className={clsx(classes.webLink, classes.row)}
                    >
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                size="1x"
                                className={classes.iconColor}
                            />
                        </div>
                        <div>
                            <Typography variant="subtitle2">
                                {formatStreetAddress}
                            </Typography>
                            <Typography variant="subtitle2">{formatCityStateZip}</Typography>
                        </div>
                    </a>
                </div>
            )}

            {data.emailAddress && (
                <div className={classes.spacing2}>
                    <a href={`mailto: ${data.emailAddress}`} className={clsx(classes.webLink, classes.row)}>
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                size="1x"
                                className={classes.iconColor}
                            />
                        </div>
                        <Typography variant="subtitle2">
                            {data.emailAddress}
                        </Typography>
                    </a>
                </div>
            )}

            {formatMobilePhoneNumber && (
                isMobile ?
                    <div className={classes.spacing2}>
                        <a href={data.mobilePhone ? `tel:${data.mobilePhone}` : `tel:${data.officePhone}`} className={clsx(classes.webLink, classes.row)}>
                            <div className={classes.iconSpace}>
                                <FontAwesomeIcon
                                    icon={faPhoneAlt}
                                    size="1x"
                                    className={classes.iconColor}
                                />
                            </div>
                            <Typography variant="subtitle2">
                                Mobile #: {formatMobilePhoneNumber}
                            </Typography>
                        </a>
                    </div>
                    :
                    <div className={clsx(classes.spacing2, classes.row)}>
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faPhoneAlt}
                                size="1x"
                                className={classes.iconColor}
                            /> {" "}
                        </div>
                        <Typography variant="subtitle2">
                            Mobile #: {formatMobilePhoneNumber}
                        </Typography>
                    </div>
            )}

            {formatOfficePhoneNumber && (
                isMobile ?
                    <div className={classes.spacing2}>
                        <a href={data.mobilePhone ? `tel:${data.mobilePhone}` : `tel:${data.officePhone}`} className={clsx(classes.webLink, classes.row)}>
                            <div className={classes.iconSpace}>
                                <FontAwesomeIcon
                                    icon={faPhoneAlt}
                                    size="1x"
                                    className={classes.iconColor}
                                />
                            </div>
                            <Typography variant="subtitle2">
                                Office #: {formatOfficePhoneNumber}
                            </Typography>
                        </a>
                    </div>
                    :
                    <div className={clsx(classes.spacing2, classes.row)}>
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faPhoneAlt}
                                size="1x"
                                className={classes.iconColor}
                            /> {" "}
                        </div>
                        <Typography variant="subtitle2">
                            Office #: {formatOfficePhoneNumber}
                        </Typography>
                    </div>
            )}

            {data.startDate && data.endDate && (
                <div onClick={() => setOpenEventModal(true)} className={classes.webLink}>
                    <div className={clsx(classes.spacing2, classes.row)}>
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faCalendarAlt}
                                size="1x"
                                className={classes.iconColor}
                            />
                        </div>
                        <Typography variant="subtitle2">
                            {startDate} to {endDate}
                        </Typography>
                    </div>
                    <div className={clsx(classes.spacing2, classes.row)}>
                        <div className={classes.iconSpace}>
                            <FontAwesomeIcon
                                icon={faClock}
                                size="1x"
                                className={classes.iconColor}
                            />
                        </div>
                        <Typography variant="subtitle2">
                            {startTime} to {endTime}
                        </Typography>
                    </div>
                </div>
            )}

            {data.seatsAvailable && (
                <div className={clsx(classes.spacing2, classes.row)}>
                    <div className={classes.iconSpace}>
                        <FontAwesomeIcon
                            icon={faUsers}
                            size="1x"
                            className={classes.iconColor}
                        />
                    </div>
                    <Typography variant="subtitle2">
                        {data.seatsAvailable} Seat(s)
                    </Typography>
                </div>
            )}

            {data.notes && (
                <div className={clsx(classes.spacing2, classes.row)}>
                    <div className={classes.iconSpace}>
                        <FontAwesomeIcon
                            icon={faClipboard}
                            size="1x"
                            className={classes.iconColor}
                        />
                    </div>
                    <Typography variant="subtitle2">
                        {data.notes}
                    </Typography>
                </div>
            )}

            {data[altDisplayColumn] && altDisplayColumn !== "company" && (
                <div className={clsx(classes.spacing2, classes.container)}>
                    <a 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        href={data[altDisplayColumn].includes("http") ? data[altDisplayColumn] : null} 
                        className={data[altDisplayColumn].includes("http") ? classes.webLink : null}
                    >
                        <Typography variant="subtitle2" className={classes.textWrap}>
                            {data[altDisplayColumn]}
                        </Typography>
                    </a>
                </div>
            )}

            <Modal
                open={openEventModal}
                onClose={() => setOpenEventModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style} className={classes.modalContainer}>
                    <div className={classes.closeButton}>
                        <FontAwesomeIcon
                            icon={faTimesCircle}
                            size="2x"
                            className={clsx(classes.iconColor, classes.webLink)}
                            onClick={() =>  setOpenEventModal(false)}
                        />
                    </div>
                    <Typography variant="h6">Add Event:</Typography>
                    <a target="_blank" rel="noopener noreferrer" href={outlookCalendarOfficeEvent} className={clsx(classes.webLink, classes.calendarButton)}>
                        <FontAwesomeIcon
                            icon={faMicrosoft}
                            size="1x"
                            className={classes.iconColor}
                        />
                        <Typography color="primary">GMR Outlook Calendar</Typography>
                    </a>
                    <a target="_blank" rel="noopener noreferrer" href={outlookCalendarLiveEvent} className={clsx(classes.webLink, classes.calendarButton)}>
                        <FontAwesomeIcon
                            icon={faMicrosoft}
                            size="1x"
                            className={classes.iconColor}
                        />
                        <Typography color="primary">Personal Outlook Calendar</Typography>
                    </a>
                    <a 
                        target="_blank" 
                        rel="noopener noreferrer"
                        href={isMobile ? googleCalendarMobileEvent : googleCalendarEvent} 
                        className={clsx(classes.webLink, classes.calendarButton)}
                    >
                        <FontAwesomeIcon
                            icon={faGoogle}
                            size="1x"
                            className={classes.iconColor}
                        />
                        <Typography color="primary">Google Calendar</Typography>
                    </a>
                    {/* <div onClick={handleSave} className={clsx(classes.webLink, classes.calendarButton)}>
                        <FontAwesomeIcon
                            icon={faFileDownload}
                            size="1x"
                            className={classes.iconColor}
                        />
                        <Typography color="primary" className={classes.container}>Add event to calendar by downloading ICS file.</Typography>
                    </div> */}
                </Box>
            </Modal>
        </div>
    )
}

export default ModuleList;
